/**
 * Organizations Service
 *
 * Type-safe organization management service using @monorepo/shared schemas
 */

import {
  type CreateOrganization,
  CreateOrganizationSchema,
  type OrganizationQuery,
  type OrganizationResponse,
  OrganizationResponseSchema,
  type PaginatedOrganizationsResponse,
  PaginatedOrganizationsResponseSchema,
  type UpdateOrganization,
  UpdateOrganizationSchema,
} from '@monorepo/shared/schemas/customers'
import { del, get, post, put } from '@/lib/api-client'

export const organizationsService = {
  /**
   * List all organizations (filtered by query)
   */
  async getOrganizations(
    query?: OrganizationQuery
  ): Promise<PaginatedOrganizationsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.external_id) queryParams.append('external_id', query.external_id)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/organizations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedOrganizationsResponseSchema)
  },

  /**
   * Get organization by ID
   */
  async getOrganization(id: string): Promise<OrganizationResponse> {
    return get(`/organizations/${id}`, OrganizationResponseSchema)
  },

  /**
   * Create new organization
   */
  async createOrganization(
    data: CreateOrganization
  ): Promise<OrganizationResponse> {
    const validated = CreateOrganizationSchema.parse(data)

    return post('/organizations', validated, OrganizationResponseSchema)
  },

  /**
   * Update organization
   */
  async updateOrganization(
    id: string,
    data: UpdateOrganization
  ): Promise<OrganizationResponse> {
    const validated = UpdateOrganizationSchema.parse(data)

    return put(`/organizations/${id}`, validated, OrganizationResponseSchema)
  },

  /**
   * Delete organization (soft delete)
   */
  async deleteOrganization(id: string): Promise<void> {
    await del(`/organizations/${id}`)
  },

  // ========================================================================
  // TAX MANAGEMENT
  // ========================================================================

  /**
   * Assign taxes to organization (default taxes for all customers in org)
   */
  async assignTaxes(
    organizationId: string,
    data: { tax_ids: string[]; replace_existing?: boolean }
  ): Promise<{
    organization_id: string
    organization_name: string
    taxes: Array<{
      id: string
      code: string
      name: string
      tax_type: 'percentage' | 'fixed' | 'compound'
      percentage_value: number | null
      fixed_amount_cents: number | null
      priority: number
      is_active: boolean
      assigned_at: string
    }>
  }> {
    return post(`/organizations/${organizationId}/taxes`, data)
  },

  /**
   * Get taxes assigned to organization
   */
  async getOrganizationTaxes(organizationId: string): Promise<{
    organization_id: string
    organization_name: string
    taxes: Array<{
      id: string
      code: string
      name: string
      tax_type: 'percentage' | 'fixed' | 'compound'
      percentage_value: number | null
      fixed_amount_cents: number | null
      priority: number
      is_active: boolean
      assigned_at: string
    }>
  }> {
    return get(`/organizations/${organizationId}/taxes`)
  },

  /**
   * Remove tax from organization
   */
  async removeTax(
    organizationId: string,
    taxId: string
  ): Promise<{
    success: boolean
    tax_id: string
    message: string
  }> {
    return del(`/organizations/${organizationId}/taxes/${taxId}`)
  },
}
